/*
 * /telemedicine/$patientId — practitioner/admin view
 * Manages the full session lifecycle: start → waiting room → admit → video call → end.
 */
import {
	Button,
	toast,
} from "@emr/react-ui"
import type { TelemedicineSession } from "@emr/db/client"
import { createFileRoute } from "@tanstack/react-router"
import {
	CheckCircle2,
	Layers,
	Loader2,
	MessageSquare,
	Mic,
	MicOff,
	PhoneOff,
	User,
	Video,
	VideoOff,
} from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { useAuth } from "@emr/auth/client"
import { TelemedicineChatPanel } from "../../components/telemedicine-chat-panel"
import { trpc } from "../../trpc"
import { rolePageLoader } from "../../utils/auth.utils"
import {
	DefaultMeetingSession,
	ConsoleLogger,
	LogLevel,
	MeetingSessionConfiguration,
	DefaultDeviceController,
} from "amazon-chime-sdk-js"

export const Route = createFileRoute("/telemedicine/$patientId")({
	component: TelemedicinePage,
	loader: rolePageLoader(["practitioner", "nurse", "admin", "super_admin"]),
})

/* ─── Types ──────────────────────────────────────────────────────────────── */

type CallState = "idle" | "starting" | "waiting_patient" | "waiting_admission" | "connecting" | "active" | "ended"

/* ─── Background options ─────────────────────────────────────────────────── */

const BACKGROUNDS = [
	{ id: "none",   label: "None",        color: "" },
	{ id: "blur",   label: "Blur",        color: "#d1d5db" },
	{ id: "office", label: "Office",      color: "#1e3a5f" },
	{ id: "nature", label: "Nature",      color: "#14532d" },
	{ id: "plain",  label: "Plain White", color: "#f8fafc" },
]

/* ─── Page ───────────────────────────────────────────────────────────────── */

function TelemedicinePage() {
	const { patientId } = Route.useParams()
	const { user } = useAuth()

	const [callState, setCallState] = useState<CallState>("idle")
	const [session, setSession]     = useState<TelemedicineSession | null>(null)
	const [micOn, setMicOn]         = useState(true)
	const [camOn, setCamOn]         = useState(true)
	const [elapsed, setElapsed]     = useState(0)
	const [selectedBg, setSelectedBg] = useState("none")
	const [showBgPanel, setShowBgPanel] = useState(false)
	const [showChat, setShowChat]   = useState(false)
	const [hasUnread, setHasUnread] = useState(false)
	const lastSeenMsgCountRef       = useRef(0)

	const meetingSessionRef = useRef<DefaultMeetingSession | null>(null)
	const localVideoRef     = useRef<HTMLVideoElement>(null)
	const remoteVideoRef    = useRef<HTMLVideoElement>(null)
	const elapsedRef        = useRef<ReturnType<typeof setInterval> | null>(null)

	const { data: patient } = trpc.patient.getByFilter.useQuery({ where: { id: patientId } })

	// Poll for new chat messages while chat is closed to show the unread dot
	const { data: chatPreview } = trpc.telemedicineChat.getBySession.useQuery(
		{ sessionId: session?.id ?? "" },
		{ enabled: !showChat && !!session?.id, refetchInterval: 4000 },
	)

	useEffect(() => {
		if (!chatPreview) return
		const nonMine = chatPreview.filter((m) => m.senderId !== user?.id)
		if (nonMine.length > lastSeenMsgCountRef.current) setHasUnread(true)
	}, [chatPreview, user?.id])

	function openChat() {
		setShowChat(true)
		setHasUnread(false)
		lastSeenMsgCountRef.current = chatPreview?.filter((m) => m.senderId !== user?.id).length ?? 0
	}

	/* ─── Helper functions (declared before mutations that call them) ────── */

	function startElapsed() {
		if (elapsedRef.current) clearInterval(elapsedRef.current)
		setElapsed(0)
		elapsedRef.current = setInterval(() => setElapsed((s) => s + 1), 1000)
	}

	function disconnectChime() {
		if (meetingSessionRef.current) {
			try {
				const av = meetingSessionRef.current.audioVideo
				av.stopLocalVideoTile()
				av.stopVideoInput()
				av.stopAudioInput()
				av.stop()
			} catch {}
			meetingSessionRef.current = null
		}
		if (elapsedRef.current) {
			clearInterval(elapsedRef.current)
			elapsedRef.current = null
		}
	}

	async function connectChime(meetingData: any, attendeeData: any) {
		const logger = new ConsoleLogger("TeleMed", LogLevel.WARN)
		const deviceController = new DefaultDeviceController(logger)
		const config = new MeetingSessionConfiguration(meetingData, attendeeData)
		const ms = new DefaultMeetingSession(config, logger, deviceController)
		meetingSessionRef.current = ms

		const videoDevices = await ms.audioVideo.listVideoInputDevices()
		const audioDevices = await ms.audioVideo.listAudioInputDevices()
		if (videoDevices[0]) await ms.audioVideo.startVideoInput(videoDevices[0].deviceId)
		if (audioDevices[0]) await ms.audioVideo.startAudioInput(audioDevices[0].deviceId)

		ms.audioVideo.addObserver({
			videoTileDidUpdate(tileState) {
				if (tileState.localTile) {
					ms.audioVideo.bindVideoElement(tileState.tileId!, localVideoRef.current!)
				} else if (tileState.boundAttendeeId) {
					ms.audioVideo.bindVideoElement(tileState.tileId!, remoteVideoRef.current!)
				}
			},
			audioVideoDidStop() {
				setCallState((s) => s === "active" ? "ended" : s)
			},
		})

		ms.audioVideo.start()
		ms.audioVideo.startLocalVideoTile()
	}

	/* ─── Mutations ──────────────────────────────────────────────────────── */

	const start = trpc.telemedicine.start.useMutation({
		onSuccess: async (data) => {
			setSession(data.session)
			setCallState("connecting")
			await connectChime(data.meeting, data.attendee)
			setCallState("waiting_patient")
			toast.success("Session started — waiting for patient to join")
		},
		onError: (e) => {
			toast.error(e.message)
			setCallState("idle")
		},
	})

	const admit = trpc.telemedicine.admit.useMutation({
		onSuccess: () => {
			setCallState("active")
			startElapsed()
			toast.success("Patient admitted")
		},
		onError: (e) => toast.error(e.message),
	})

	const end = trpc.telemedicine.end.useMutation({
		onSuccess: (data) => {
			disconnectChime()
			setCallState("ended")
			if (data.durationSeconds != null) {
				toast.success(`Session ended — ${formatDuration(data.durationSeconds)}`)
			}
		},
		onError: (e) => toast.error(e.message),
	})

	/* ─── Polling + side effects ─────────────────────────────────────────── */

	const { data: activeSession } = trpc.telemedicine.getActiveByPatient.useQuery(
		{ patientId },
		{
			enabled: callState === "waiting_patient" || callState === "waiting_admission",
			refetchInterval: 3000,
		},
	)

	useEffect(() => {
		if (!activeSession) return
		if (activeSession.status === "waiting_admission" && callState === "waiting_patient") {
			setSession(activeSession)
			setCallState("waiting_admission")
			toast("Patient is in the waiting room", { icon: "👤" })
		}
	}, [activeSession?.status, callState])

	useEffect(() => () => {
		if (elapsedRef.current) clearInterval(elapsedRef.current)
		disconnectChime()
	}, [])

	/* ─── Control handlers ───────────────────────────────────────────────── */

	function toggleMic() {
		const ms = meetingSessionRef.current
		if (!ms) return
		if (micOn) ms.audioVideo.realtimeMuteLocalAudio()
		else ms.audioVideo.realtimeUnmuteLocalAudio()
		setMicOn((v) => !v)
	}

	function toggleCam() {
		const ms = meetingSessionRef.current
		if (!ms) return
		if (camOn) ms.audioVideo.stopLocalVideoTile()
		else ms.audioVideo.startLocalVideoTile()
		setCamOn((v) => !v)
	}

	function handleEnd() {
		if (!session) return
		end.mutate({ sessionId: session.id })
	}

	function handleStart() {
		setCallState("starting")
		start.mutate({ patientId })
	}

	function handleAdmit() {
		if (!session) return
		admit.mutate({ sessionId: session.id })
	}

	const patientName = patient ? `${patient.firstName} ${patient.lastName}` : "Patient"
	const sessionActive = callState === "waiting_patient" || callState === "waiting_admission" || callState === "active"

	return (
		<div className="fixed inset-0 z-50 bg-gray-950 flex flex-col">
			{/* ─── Header ─────────────────────────────────────────────────── */}
			<div className="flex items-center justify-between px-6 py-4 bg-gray-900 border-b border-gray-800 shrink-0">
				<div className="flex items-center gap-3">
					<div className="w-9 h-9 rounded-full bg-primary/20 flex items-center justify-center">
						<User className="w-5 h-5 text-primary" />
					</div>
					<div>
						<p className="text-sm font-semibold text-white">{patientName}</p>
						<p className="text-xs text-gray-400">
							{callState === "idle"              && "Ready to start"}
							{callState === "starting"          && "Starting session…"}
							{callState === "connecting"        && "Connecting…"}
							{callState === "waiting_patient"   && "Waiting for patient to join"}
							{callState === "waiting_admission" && "Patient is waiting — ready to admit"}
							{callState === "active"            && `In session · ${formatDuration(elapsed)}`}
							{callState === "ended"             && "Session ended"}
						</p>
					</div>
				</div>

				<div className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full ${
					callState === "active"             ? "bg-green-900/60 text-green-300" :
					callState === "waiting_admission"  ? "bg-amber-900/60 text-amber-300" :
					callState === "waiting_patient"    ? "bg-blue-900/60 text-blue-300" :
					"bg-gray-800 text-gray-400"
				}`}>
					<span className={`w-2 h-2 rounded-full ${
						callState === "active"            ? "bg-green-400 animate-pulse" :
						callState === "waiting_admission" ? "bg-amber-400 animate-pulse" :
						callState === "waiting_patient"   ? "bg-blue-400 animate-pulse" :
						"bg-gray-600"
					}`} />
					{callState === "active"            ? "Live" :
					 callState === "waiting_admission" ? "Patient waiting" :
					 callState === "waiting_patient"   ? "Waiting" : ""}
				</div>
			</div>

			{/* ─── Body (video + optional chat panel) ─────────────────────── */}
			<div className="flex-1 flex min-h-0">
				{/* Video area */}
				<div className="flex-1 relative bg-black overflow-hidden">
					{/* Remote video (patient) */}
					<video
						ref={remoteVideoRef}
						autoPlay
						playsInline
						className="absolute inset-0 w-full h-full object-cover"
						style={{ display: callState === "active" ? "block" : "none" }}
					/>

					{/* Waiting / idle state overlay */}
					{callState !== "active" && (
						<div className="absolute inset-0 flex flex-col items-center justify-center gap-6">
							{callState === "idle" && (
								<>
									<div className="w-24 h-24 rounded-full bg-gray-800 flex items-center justify-center">
										<Video className="w-12 h-12 text-gray-600" />
									</div>
									<p className="text-gray-400 text-lg">Ready to start a telemedicine session</p>
									<Button size="lg" onClick={handleStart} disabled={start.isPending} className="gap-2">
										{start.isPending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Video className="w-5 h-5" />}
										Start Session
									</Button>
								</>
							)}

							{(callState === "starting" || callState === "connecting") && (
								<>
									<Loader2 className="w-12 h-12 text-primary animate-spin" />
									<p className="text-gray-400">Setting up session…</p>
								</>
							)}

							{callState === "waiting_patient" && (
								<>
									<div className="w-24 h-24 rounded-full bg-blue-900/30 flex items-center justify-center animate-pulse">
										<User className="w-12 h-12 text-blue-400" />
									</div>
									<p className="text-gray-300 text-lg font-medium">Waiting for patient to join</p>
									<p className="text-gray-500 text-sm">A notification has been sent to {patientName}</p>
								</>
							)}

							{callState === "waiting_admission" && (
								<>
									<div className="w-24 h-24 rounded-full bg-amber-900/30 flex items-center justify-center">
										<CheckCircle2 className="w-12 h-12 text-amber-400" />
									</div>
									<p className="text-gray-200 text-xl font-semibold">{patientName} is in the waiting room</p>
									<p className="text-gray-400 text-sm">Click Admit to start the session</p>
									<Button
										size="lg"
										onClick={handleAdmit}
										disabled={admit.isPending}
										className="gap-2 bg-green-600 hover:bg-green-500"
									>
										{admit.isPending ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />}
										Admit Patient
									</Button>
								</>
							)}

							{callState === "ended" && (
								<>
									<div className="w-24 h-24 rounded-full bg-gray-800 flex items-center justify-center">
										<PhoneOff className="w-12 h-12 text-gray-500" />
									</div>
									<p className="text-gray-400 text-lg">Session ended</p>
									<Button variant="outline" onClick={() => window.history.back()}>Back</Button>
								</>
							)}
						</div>
					)}

					{/* Local video (self — PiP) */}
					<video
						ref={localVideoRef}
						autoPlay
						playsInline
						muted
						className="absolute bottom-6 right-6 w-44 h-32 rounded-xl object-cover border-2 border-gray-700 bg-gray-800 shadow-xl"
						style={{ display: sessionActive ? "block" : "none" }}
					/>

					{/* Background selection panel */}
					{showBgPanel && (
						<div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-gray-900/95 backdrop-blur rounded-2xl p-4 flex items-center gap-3 border border-gray-700 shadow-2xl">
							<Layers className="w-4 h-4 text-gray-400" />
							<span className="text-xs text-gray-400 mr-1">Background</span>
							{BACKGROUNDS.map((bg) => (
								<button
									key={bg.id}
									onClick={() => { setSelectedBg(bg.id); setShowBgPanel(false) }}
									className={`w-10 h-7 rounded-lg border-2 text-xs transition-all ${selectedBg === bg.id ? "border-primary scale-110" : "border-gray-600 hover:border-gray-400"}`}
									style={{ backgroundColor: bg.color || "#374151" }}
									title={bg.label}
								/>
							))}
						</div>
					)}
				</div>

				{/* Chat panel (right side, when session is active) */}
				{showChat && session && (
					<TelemedicineChatPanel
						sessionId={session.id}
						myUserId={user?.id}
						onClose={() => setShowChat(false)}
						className="w-80 h-full"
					/>
				)}
			</div>

			{/* ─── Controls bar ────────────────────────────────────────────── */}
			{sessionActive && (
				<div className="flex items-center justify-center gap-4 py-5 bg-gray-900 border-t border-gray-800 shrink-0">
					<ControlBtn active={micOn} onClick={toggleMic} icon={<Mic className="w-5 h-5" />} offIcon={<MicOff className="w-5 h-5" />} label="Mic" />
					<ControlBtn active={camOn} onClick={toggleCam} icon={<Video className="w-5 h-5" />} offIcon={<VideoOff className="w-5 h-5" />} label="Camera" />
					<ControlBtn active={showBgPanel} onClick={() => setShowBgPanel((v) => !v)} icon={<Layers className="w-5 h-5" />} offIcon={<Layers className="w-5 h-5" />} label="Background" />
					{/* Chat toggle */}
					<button onClick={showChat ? () => setShowChat(false) : openChat} className="flex flex-col items-center gap-1 relative">
						<span className={`w-14 h-14 rounded-full flex items-center justify-center text-white transition-colors ${showChat ? "bg-primary hover:bg-primary/90" : "bg-gray-700 hover:bg-gray-600"}`}>
							<MessageSquare className="w-5 h-5" />
						</span>
						{hasUnread && !showChat && (
							<span className="absolute top-0 right-0 w-3 h-3 bg-red-500 rounded-full border-2 border-gray-900" />
						)}
						<span className="text-xs text-gray-400">Chat</span>
					</button>
					{callState === "active" && (
						<button onClick={handleEnd} disabled={end.isPending} className="flex flex-col items-center gap-1">
							<span className="w-14 h-14 rounded-full bg-red-600 hover:bg-red-500 flex items-center justify-center text-white transition-colors">
								<PhoneOff className="w-6 h-6" />
							</span>
							<span className="text-xs text-gray-400">End</span>
						</button>
					)}
				</div>
			)}
		</div>
	)
}

/* ─── Helpers ─────────────────────────────────────────────────────────────── */

function ControlBtn({
	active,
	onClick,
	icon,
	offIcon,
	label,
}: {
	active: boolean
	onClick: () => void
	icon: React.ReactNode
	offIcon: React.ReactNode
	label: string
}) {
	return (
		<button onClick={onClick} className="flex flex-col items-center gap-1">
			<span className={`w-14 h-14 rounded-full flex items-center justify-center text-white transition-colors ${active ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-800/80 hover:bg-gray-700 text-gray-400"}`}>
				{active ? icon : offIcon}
			</span>
			<span className="text-xs text-gray-400">{label}</span>
		</button>
	)
}

function formatDuration(s: number) {
	const m = Math.floor(s / 60)
	const ss = s % 60
	return `${m}:${String(ss).padStart(2, "0")}`
}
